<section class="import-page">
  <header>
    <button mat-button routerLink="/invoices"><mat-icon>arrow_back</mat-icon> Invoices</button>
    <p class="eyebrow">Knowledge library</p>
    <h1>Invoice memory import</h1>
    <p class="intro">Turn your invoice history into searchable memories of devices, faults, parts, services and recorded outcomes.</p>
  </header>
  <p role="alert" class="error" *ngIf="error || loadError">{{ error || loadError }}</p>
  <mat-spinner diameter="36" *ngIf="!status && loading"></mat-spinner>
  <ng-container *ngIf="status as s">
    <div class="card setup">
      <div>
        <h2>One pass through existing invoices</h2>
        <p>Starting sends each invoice's line items and relevant device/task details to OpenAI using <strong>{{ s.model }}</strong>. API usage is charged to your configured OpenAI account.</p>
        <p>The run continues on the server when this page is closed. Saved memories stay available through MCP. Existing memories and invoice details are preserved.</p>
      </div>
      <div class="actions">
        <button mat-raised-button color="primary" *ngIf="s.state === 'not_started'" [disabled]="busy || !s.total" (click)="act('start')">Generate invoice memories</button>
        <button mat-raised-button color="primary" *ngIf="s.canResume && (s.state === 'paused' || s.state === 'interrupted')" [disabled]="busy || coolingDown" (click)="act('start')">Resume import</button>
        <button mat-stroked-button *ngIf="s.state === 'running'" [disabled]="busy || s.pauseRequested" (click)="act('pause')">{{ s.pauseRequested ? 'Pausing after current invoice…' : 'Pause after current invoice' }}</button>
        <button mat-stroked-button color="primary" *ngIf="s.canResume && s.failed > 0" [disabled]="busy || coolingDown" (click)="act('retry')">Retry {{ s.failed }} failed invoices</button>
      </div>
    </div>
    <div class="card">
      <div class="progress-heading"><h2>{{ s.preparing ? 'Preparing invoice list' : (s.state | titlecase) }}</h2><span>{{ progress }}%</span></div>
      <mat-progress-bar [mode]="s.preparing ? 'indeterminate' : 'determinate'" [value]="progress"></mat-progress-bar>
      <p *ngIf="s.currentInvoice">Processing invoice #{{ s.currentInvoice }}</p>
      <p *ngIf="s.state === 'interrupted'">The server stopped before finishing. Resume to continue from the saved progress.</p>
      <p *ngIf="s.state === 'completed' && !s.failed">The pass is complete. Generated summaries can now be searched with invoice memory MCP tools.</p>
      <p role="alert" class="error" *ngIf="s.error">{{ s.error }}</p>
      <p *ngIf="s.retryAt && coolingDown">{{ s.state === 'running' ? 'Automatic retry at' : 'Retry available after' }} {{ s.retryAt | date:'medium' }}. {{ s.state === 'running' ? 'You can pause while waiting.' : '' }}</p>
      <p *ngIf="quotaIssue">
        <a href="https://platform.openai.com/settings/organization/billing/overview" target="_blank" rel="noopener">OpenAI API billing</a>
        · <a href="https://platform.openai.com/settings/organization/limits" target="_blank" rel="noopener">API limits</a>
      </p>
      <div class="stats">
        <div><strong>{{ s.total }}</strong><span>Invoices in this pass</span></div>
        <div><strong>{{ s.saved }}</strong><span>Memories saved</span></div>
        <div><strong>{{ s.pending }}</strong><span>Remaining</span></div>
        <div><strong>{{ s.failed }}</strong><span>Failed</span></div>
        <div><strong>{{ s.skipped }}</strong><span>Skipped</span></div>
      </div>
      <p class="muted" *ngIf="s.inputTokens || s.outputTokens">Recorded usage: {{ s.inputTokens | number }} input tokens · {{ s.outputTokens | number }} output tokens</p>
    </div>
    <div class="card" *ngIf="s.records.length">
      <h2>Recent results</h2>
      <article *ngFor="let record of s.records">
        <div class="result-heading">
          <a *ngIf="record.invoiceNumber" [routerLink]="['/edit-invoice', record.invoiceNumber]">Invoice #{{ record.invoiceNumber }}</a>
          <span *ngIf="!record.invoiceNumber">Invoice without a valid number</span>
          <span class="badge" [class.failed]="record.state === 'failed'">{{ record.state | titlecase }}</span>
          <time>{{ record.updatedAt | date:'short' }}</time>
        </div>
        <p class="summary">{{ record.error || record.summary }}</p>
        <div class="tags"><span *ngFor="let tag of record.tags">{{ tag }}</span></div>
      </article>
      <div class="pagination">
        <button mat-button [disabled]="!offset || loading" (click)="page(-1)">Newer</button>
        <span>Results {{ offset + 1 }}–{{ offset + s.records.length }}</span>
        <button mat-button [disabled]="s.records.length < 20 || loading || offset >= 100000" (click)="page(1)">Older</button>
      </div>
    </div>
  </ng-container>
</section>
